import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LinkAsButton } from "@/components/LinkAsButton";
import { EntityListPage } from "@/components/entity-page/EntityListPage";
import { getIntegrationTypes } from "@/lib/formOptions";
import type { FormOption } from "@/types";

export const Route = createFileRoute("/dashboard/integration-types/")({
  component: IntegrationTypesIndexPage,
});

function isEnabled(integrationType: FormOption): boolean {
  const metadata =
    typeof integrationType.metadata === "string"
      ? safeParseMetadata(integrationType.metadata)
      : integrationType.metadata;
  return metadata?.enabled !== false;
}

function safeParseMetadata(raw: string): Record<string, unknown> {
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function IntegrationTypesIndexPage() {
  const [integrationTypes, setIntegrationTypes] = useState<FormOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getIntegrationTypes()
      .then((options) => {
        if (!active) return;
        setIntegrationTypes(options);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Error fetching integration types:", err);
        setError("Failed to load integration types.");
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <EntityListPage
      title="Integrations"
      description="Manage the catalog of integration types clients can install (e.g. Calendly, GoHighLevel)."
      items={integrationTypes}
      loading={loading}
      error={error}
      getItemKey={(integrationType) => integrationType.id}
      gridClassName="grid grid-cols-1 gap-4"
      renderItem={(integrationType) => (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <CardTitle>{integrationType.label}</CardTitle>
                  <Badge variant={isEnabled(integrationType) ? "default" : "secondary"}>
                    {isEnabled(integrationType) ? "Enabled" : "Disabled"}
                  </Badge>
                </div>
                <CardDescription>
                  {integrationType.description || `Slug: ${integrationType.value}`}
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to="/dashboard/integration-types/$id" params={{ id: integrationType.id }}>
                  Edit
                </Link>
              </Button>
            </div>
          </CardHeader>
        </Card>
      )}
      createHref="/dashboard/integration-types/new"
      createLabel="Create Integration Type"
      emptyMessage="No integration types yet. Create one to get started."
      headerActions={
        <LinkAsButton to="/dashboard/settings" variant="ghost" size="sm">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Settings
        </LinkAsButton>
      }
    />
  );
}
